import React, { createContext, useContext, useEffect, useState } from "react";
import { notification } from "antd";
import { useIdleTimer, updateLastActive } from "./useIdleTimer";
import WarningModal from "../overlays/sessionWarning";
import SessionExpiredModal from "../overlays/sessionExpired";
import { useAuth } from "../msalAuth/AuthProvider";

const IDLE_WARNING_TIME = 25 * 60 * 1000; // 25 min
const IDLE_EXPIRATION_TIME = 30 * 60 * 1000; // 30 min
// const IDLE_WARNING_TIME = 5 * 60 * 1000; // 5 min
// const IDLE_EXPIRATION_TIME = 10 * 60 * 1000; // 10 min
const IdleTimerContext = createContext();

/**
 * This provider handles the idle logic:
 * - 25-min warning overlay
 * - 30-min expired overlay
 * - multi-tab synchronization
 * - visibility/focus checks
 * - user interactions
 */
export function IdleTimerProvider({ children }) {
  const [showWarning, setShowWarning] = useState(false);
  const [warningMsLeft, setWarningMsLeft] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const { doSilentRenew } = useAuth();

  // ============================
  // IDLE EVENTS from useIdleTimer
  // ============================
  // Called at 25-min
  const handleWarning = (timeLeft) => {
    setShowWarning(true);
    setIsWarningModalOpen(true);
    setWarningMsLeft(timeLeft);

    const globalState = getGlobalIdleState() || {
      lastActive: Date.now(),
      isExpired: false,
      warningShown: false,
    };
    const updated = {
      ...globalState,
      warningShown: true,
    };
    localStorage.setItem("SANDEE_GLOBAL_IDLE_STATE", JSON.stringify(updated));
    broadcastChange(updated);
  };

  // Called at 30-min
  const handleExpire = () => {
    setIsExpired(true);
    setShowWarning(false);
    setIsWarningModalOpen(false);
  };

  // Called on user activity
  const handleActiveUpdate = () => {
    // If the warning modal is open or session expired, skip normal activity-based reset
    if (!isWarningModalOpen && !isExpired) {
      updateLastActive(false);
    }
  };

  useIdleTimer(handleWarning, handleExpire, handleActiveUpdate);

  // ============================
  // MULTI-TAB EXPIRED RESTORATION
  // ============================
  // If user re-logs in another tab => isExpired=false => remove overlay
  useEffect(() => {
    const storageListener = (e) => {
      if (e.key === "SANDEE_GLOBAL_IDLE_STATE" && e.newValue) {
        const parsed = JSON.parse(e.newValue);

        // If we were expired, but parsed says isExpired=false => remove overlay
        if (isExpired && parsed.isExpired === false) {
          setIsExpired(false);
          notification.info({
            message: "Session Restored",
            description: "You have been re-authenticated in another tab.",
            duration: 0,
          });
        }

        // If user in another tab sets warningShown=false => hide the overlay here too
        if (showWarning && parsed.warningShown === false) {
          setShowWarning(false);
          setIsWarningModalOpen(false);
        }
      }
    };
    window.addEventListener("storage", storageListener);

    return () => {
      window.removeEventListener("storage", storageListener);
    };
  }, [isExpired, showWarning]);

  // ============================
  // FOCUS/VISIBILITY FIX
  // ============================
  useEffect(() => {
    const handleVisibilityFocusChange = () => {
      if (document.visibilityState === "visible") {
        checkIfIdleWentPastThreshold();
      }
    };

    window.addEventListener("focus", handleVisibilityFocusChange);
    document.addEventListener("visibilitychange", handleVisibilityFocusChange);

    return () => {
      window.removeEventListener("focus", handleVisibilityFocusChange);
      document.removeEventListener("visibilitychange", handleVisibilityFocusChange);
    };
  }, []);

  function checkIfIdleWentPastThreshold() {
    const globalState = getGlobalIdleState();
    if (!globalState) return;
    const now = Date.now();
    const idleTime = now - globalState.lastActive;

    // if user is already beyond 30 min
    if (idleTime >= IDLE_EXPIRATION_TIME && !globalState.isExpired) {
      handleExpire();
    }
    // else if user is between 25 - 30 min => show warning if not shown
    else if (idleTime >= IDLE_WARNING_TIME && idleTime < IDLE_EXPIRATION_TIME && !globalState.isExpired) {
      if (!globalState.warningShown) {
        // If localStorage says not shown, set it now
        const updated = { ...globalState, warningShown: true };
        localStorage.setItem("SANDEE_GLOBAL_IDLE_STATE", JSON.stringify(updated));
        broadcastChange(updated);
      }
      // Show overlay in React state if not already
      if (!showWarning) {
        const timeLeft = IDLE_EXPIRATION_TIME - idleTime;
        setShowWarning(true);
        setIsWarningModalOpen(true);
        setWarningMsLeft(timeLeft);
      }
    }
  }

  function getGlobalIdleState() {
    const data = localStorage.getItem("SANDEE_GLOBAL_IDLE_STATE");
    return data ? JSON.parse(data) : null;
  }

  function broadcastChange(payload) {
    if (typeof BroadcastChannel !== "undefined") {
      const bc = new BroadcastChannel("IDLE_CHANNEL");
      bc.postMessage({ type: "IDLE_UPDATE", payload });
      bc.close();
    }
  }

  // ============================
  // ON MOUNT
  // ============================
  useEffect(() => {
    const globalState = getGlobalIdleState();
    if (globalState) {
      const now = Date.now();
      const idleTime = now - globalState.lastActive;
      if (idleTime >= IDLE_WARNING_TIME && idleTime < IDLE_EXPIRATION_TIME && !globalState.isExpired) {
        // We are in 25-30 min window
        setShowWarning(true);
        setIsWarningModalOpen(true);
        setWarningMsLeft(IDLE_EXPIRATION_TIME - idleTime);
      } else if (idleTime >= IDLE_EXPIRATION_TIME) {
        handleExpire();
      }
    }
  }, []);

  // ============================
  // 25-MIN MODAL ACTIONS
  // ============================
  async function handleOverlayDismiss() {
    // User clicked "Continue" or "(x)"

    // 1) Set warningShown=false in localStorage
    const globalState = getGlobalIdleState() || {
      lastActive: Date.now(),
      isExpired: false,
      warningShown: true,
    };
    const now = Date.now();
    const updated = {
      ...globalState,
      warningShown: false,
      lastActive: now,
      isExpired: false,
    };
    localStorage.setItem("SANDEE_GLOBAL_IDLE_STATE", JSON.stringify(updated));
    broadcastChange(updated);

    // 2) Remove overlay in this tab
    setShowWarning(false);
    setIsWarningModalOpen(false);

    // 3) updateLastActive(true) => reset idle
    updateLastActive(true);

    // 4) do silent renew
    await doSilentRenew();
  }

  const onContinueSession = handleOverlayDismiss;
  const onCloseWarningModal = handleOverlayDismiss;

  return (
    <IdleTimerContext.Provider value={{ isExpired }}>
      {children}

      {/* The warning overlay shows if showWarning && not isExpired */}
      <WarningModal open={showWarning && !isExpired} timeRemainingMs={warningMsLeft} onContinue={onContinueSession} onCloseX={onCloseWarningModal} />

      {isExpired && <SessionExpiredModal />}
    </IdleTimerContext.Provider>
  );
}

export function useIdleTimerContext() {
  return useContext(IdleTimerContext);
}
